/* eslint-disable no-param-reassign */
import Excel from "exceljs";
// @ts-expect-error: Ignoring because it wants us to update the tsconfig.json to include "resolveJsonModule", which is not worth it just for this
import filenamify from "filenamify/browser";
import dayjs from "dayjs";
import { downloadFile, saveAs } from "@/utils/blob";
import {
  EmployeeTimesheetData,
  EntryCategory,
  TimesheetEntry,
} from "../types/Timesheet";

const CategoryColumnMap: Record<EntryCategory, number> = {
  REGULAR: 3,
  OVERTIME: 4,
  DOUBLE: 5,
  FIELD: 6,
  FIELD_OVERTIME: 7,
  FIELD_DOUBLE: 8,
  TRAVEL: 9,
  VACATION: 10,
  SICK: 11,
  BANKED_HOURS: 12,
  LOA: 13,
};

export interface PeriodGroup {
  dates: string[]; // YYYY-MM-DD dates within this period that fall in the export range
}

/**
 * Splits a date range into Thursday–Wednesday pay period groups.
 * Each group includes the full period label and only the dates within
 * [startDate, endDate] that belong to that period.
 */
export const getPeriodGroups = (
  startDate: string,
  endDate: string
): PeriodGroup[] => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const groups: PeriodGroup[] = [];

  let current = start;
  while (!current.isAfter(end)) {
    // dayjs day(): 0=Sun,1=Mon,2=Tue,3=Wed,4=Thu,5=Fri,6=Sat
    // (dayOfWeek + 3) % 7 gives days-since-Thursday: Thu=0,Fri=1,Sat=2,Sun=3,Mon=4,Tue=5,Wed=6
    const daysToThursday = (current.day() + 3) % 7;
    const periodThursday = current.subtract(daysToThursday, "day");
    const periodWednesday = periodThursday.add(6, "day");

    const rangeStart = periodThursday.isBefore(start) ? start : periodThursday;
    const rangeEnd = periodWednesday.isAfter(end) ? end : periodWednesday;

    const dates: string[] = [];
    let day = rangeStart;
    while (!day.isAfter(rangeEnd)) {
      dates.push(day.format("YYYY-MM-DD"));
      day = day.add(1, "day");
    }

    if (dates.length > 0) {
      groups.push({ dates });
    }

    current = periodWednesday.add(1, "day");
  }

  return groups;
};

interface HeaderArgs {
  worksheet: Excel.Worksheet;
  employeeInfo: {
    Name: string;
    Email: string;
    Location: string;
  };
  startDate: string;
  endDate: string;
}
const fillHeader = ({
  worksheet,
  employeeInfo,
  startDate,
  endDate,
}: HeaderArgs) => {
  for (let i = 7; i <= 13; i += 1) {
    worksheet.mergeCells(`A${i}:B${i}`);
  }

  worksheet.getCell("C7").value = dayjs(startDate).format("ddd MMM D");
  worksheet.getCell("C8").value = dayjs(endDate).format("ddd MMM D");

  worksheet.getCell("C9").value = employeeInfo.Name;
  worksheet.getCell("C10").value = employeeInfo.Email;
  worksheet.getCell("C11").value = employeeInfo.Location;
};

const insertDateRows = ({
  worksheet,
  periods,
}: {
  worksheet: Excel.Worksheet;
  periods: PeriodGroup[];
}) => {
  const dates = periods.flatMap((period) => period.dates);

  if (dates.length === 0) return;

  const applyDate = (row: Excel.Row, date: string) => {
    row.getCell("A").value = dayjs(date).format("dddd").toLocaleUpperCase();
    row.getCell("B").value = date;
    row.commit();
  };

  applyDate(worksheet.getRow(14), dates[0]);

  if (dates.length > 1) {
    // Duplicate row 14 to preserve template formatting for all subsequent rows
    worksheet.duplicateRow(14, dates.length - 1, true);
    for (let i = 1; i < dates.length; i += 1) {
      applyDate(worksheet.getRow(14 + i), dates[i]);
    }
  }

  // SUM row immediately after the last date row
  const sumRow = worksheet.getRow(14 + dates.length);
  for (let col = 3; col <= 12; col += 1) {
    const colLetter = worksheet.getColumn(col).letter;
    sumRow.getCell(col).value = {
      formula: `SUM(${colLetter}14:INDEX(${colLetter}:${colLetter}, ROW()-1))`,
    };
  }
  sumRow.commit();
};

const fillTimesheetData = ({
  worksheet,
  timesheetEntries,
}: {
  worksheet: Excel.Worksheet;
  timesheetEntries: TimesheetEntry[];
}) => {
  timesheetEntries.forEach((entry) => {
    if (!entry.type) return;
    const column = CategoryColumnMap[entry.type];

    // Find the row with the matching date in column B
    const dateCell = worksheet
      .getColumn("B")
      .values.findIndex((value) => value === entry.date);

    if (dateCell > 0) {
      const cell = worksheet.getRow(dateCell).getCell(column);
      const existingValue = cell.value || 0;
      cell.value = Number(existingValue) + entry.hours + entry.minutes / 60;
    }

    // If entry type is LOA, also fill in column M with TRUE to indicate LOA day
    if (entry.type === "LOA" && dateCell > 0) {
      const loaCell = worksheet.getRow(dateCell).getCell(13); // Column M
      loaCell.value = "TRUE";
    }
  });
};

interface ExportArgs {
  startDate: string;
  endDate: string;
  employeeTimesheetData: EmployeeTimesheetData[];
}

export const exportTimesheetToXlsx = async ({
  startDate,
  endDate,
  employeeTimesheetData,
}: ExportArgs) => {
  const response = await downloadFile("/TimesheetTemplate.xlsx");
  if (!response) {
    throw new Error("Unable to download template for timesheet");
  }
  const blob = await response.blob();
  const workbook = new Excel.Workbook();
  const arrayBuffer = await blob.arrayBuffer();
  await workbook.xlsx.load(arrayBuffer);

  const templateSheet = workbook.getWorksheet("Template_Worksheet");
  if (!templateSheet) {
    throw new Error("Template sheet not found in the workbook");
  }

  const periods = getPeriodGroups(startDate, endDate);

  // Ensure worksheet names are unique
  const usedNames = new Set<string>();
  employeeTimesheetData.forEach((item) => {
    let sheetName = filenamify(`${item.Name}_${item.Location}`);
    let counter = 1;
    while (usedNames.has(sheetName)) {
      sheetName = filenamify(`${counter}_${item.Name}_${item.Location}`);
      counter += 1;
    }
    usedNames.add(sheetName);

    // Create a new sheet for each employee by copying the template sheet
    const worksheet = workbook.addWorksheet("Temp name");
    worksheet.model = {
      ...templateSheet.model,
      name: sheetName,
    };

    fillHeader({
      worksheet,
      employeeInfo: {
        Name: item.Name,
        Email: item.Email,
        Location: item.Location,
      },
      startDate,
      endDate,
    });

    insertDateRows({
      worksheet,
      periods,
    });

    fillTimesheetData({
      worksheet,
      timesheetEntries: item.Entries,
    });
  });

  // Remove the template sheet before saving
  workbook.removeWorksheet(templateSheet.id);

  const outputBuffer = await workbook.xlsx.writeBuffer();
  const output = new Blob([outputBuffer]);
  const fileName = `VV Timesheet ${startDate} - ${endDate}.xlsx`;

  await saveAs(output, fileName);
};
